/**
 * Data access for the portal UI.
 *
 * Everything comes from the database via /api/*. There is no fixture mode:
 * it was scaffolding for building before credentials existed, and keeping it
 * meant every endpoint carried two code paths — which is how demo quotes ended
 * up showing against a real partner. Demos use a test partner (`is_test`), as
 * the spec intends.
 */

import type { BillingFrequency, Partner, PartnerMode, PricedProduct, Tier } from '@shared/types'
import type { LineSummary } from '@shared/productLines'
import type { ClaimTotals, Statement } from '@shared/commissions'
import type { BoardPackView, BoardSection } from '@shared/boardPack'

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path)
  if (res.ok) return res.json() as Promise<T>
  const body = await res.json().catch(() => ({})) as { error?: string; fix?: string; reason?: string }
  throw new Error([body.error, body.reason, body.fix].filter(Boolean).join(' ') || `${path} failed (${res.status})`)
}

async function send<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', payload?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: payload ? { 'content-type': 'application/json' } : undefined,
    body: payload ? JSON.stringify(payload) : undefined,
  })
  const body = await res.json().catch(() => ({})) as Record<string, unknown>
  if (!res.ok) {
    const problems = Array.isArray(body.problems) ? (body.problems as string[]) : []
    throw new Error(
      [body.error, ...problems, body.reason, body.fix].filter(Boolean).join('\n')
      || `${path} failed (${res.status})`,
    )
  }
  return body as T
}

// ---------------------------------------------------------------- catalogue

export interface CatalogueResponse {
  partnerId: string
  mode: PartnerMode
  tier: Tier | null
  tiers: Tier[]
  products: PricedProduct[]
}

export const fetchCatalogue = () => get<CatalogueResponse>('/api/catalogue')

// ---------------------------------------------------------------- quotes

export interface QuoteRow {
  id: string
  quoteNumber: string
  customer: string
  /** Decision 251: expired, but sent inside the open horizon — still open, shown as "price lapsed". */
  priceLapsed?: boolean
  title: string | null
  status: string
  valueSell: number
  valueBuy: number
  /** Decision 201: the sell split for the cockpit's biggest-open table (TCV = valueSell). */
  oneOffSell: number
  annualSell: number
  /** Decision 201: a renewal quote (W9R seq or renewal link) — the cockpit's new-business toggle hides these. */
  isRenewal: boolean
  ownerId: string | null
  ownerName: string | null
  validUntil: string | null
  /** Last send — resend bumps it (decision 78). */
  sentAt: string | null
  /** Decision 193: when the customer accepted (from the frozen record); null otherwise. */
  acceptedAt: string | null
  updatedAt: string
  /** Decision 128: the HubSpot deal's stage label, refreshed by the 10-min sweep. */
  dealStageLabel: string | null
  /** Decision 142: the stage ID — the kanban board's grouping key. Staff-only, like the label. */
  dealStageId: string | null
  /** Decision 171: set = split into per-site deals — the pill reads "accepted & split". */
  splitAt: string | null
  splitMode: SplitMode | null
}

/** Decision 142: one pipeline stage, in HubSpot's display order — a board column. */
export interface DealStage { id: string; label: string }

/** Decision 146: one mapped pipeline — the board shows one at a time. */
export interface DealPipeline { id: string; label: string; scopes: string[]; stages: DealStage[] }

/** Decision 112: cockpit stats — aggregated server-side over ALL quotes. */
/** Decision 201: one value bucket — count plus the three money figures. TCV ≠ one-off + ARR (TCV = one-off + annual × term). */
export interface ValueSplit { n: number; oneOff: number; arr: number; tcv: number }

export interface QuoteStats {
  /** Decision 201: true when the activity figures exclude renewal quotes (new business only). */
  newBusiness?: boolean
  months: Array<{
    month: string
    /** Decision 201: NET of revisions — quotes since superseded are excluded. */
    sent: number
    /** Decision 201: TCV of the (net) sent quotes, from their own totals. */
    sentValue: number
    /** Portal acceptances PLUS the HubSpot bookings backfill (decision 202). */
    accepted: number
    /** Decision 202: portal acceptances only — the acceptance rate's numerator. */
    acceptedPortal: number
    declined: number
    /** Accepted TCV, from the frozen acceptance record (+ backfill TCV). */
    acceptedValue: number
    acceptedOneOff: number
    acceptedArr: number
  }>
  avgDecisionDays: number | null
  decidedLast90: number
  /** Decision 204: the new-business slice of the 90-day decision figures. */
  avgDecisionDaysNewBusiness?: number | null
  decidedLast90NewBusiness?: number
  /** Decision 201: FY starts 1 April. `lastFyYtd` covers the same span a year earlier. */
  fy?: { start: string; ytd: ValueSplit; lastFyYtd: ValueSplit }
  /** Decision 203: everything accepted in the last rolling 30 days — portal quotes and backfilled HubSpot bookings. */
  recentAccepted?: Array<{
    source: 'portal' | 'hubspot'
    id: string | null
    quoteNumber: string | null
    hsDealId: string | null
    customer: string | null
    title: string | null
    kind: 'new_business' | 'renewal'
    acceptedAt: string
    ownerName: string | null
    oneOff: number
    arr: number
    tcv: number
  }>
  /** Decision 201: open (sent/viewed) quotes by HubSpot deal stage (staff) or status (partners). */
  /** Decision 215: each bucket also carries buy and margin % on TCV. (#245: the stage weight and weighted TCV are gone — the Hub's ARR Cockpit is the one weighted forecast.) */
  pipeline?: { by: 'stage' | 'status'; buckets: Array<ValueSplit & { key: string; label: string; buy: number; marginPct: number | null }> }
}

export interface QuotesResponse {
  /** Decision 161: the TRUE matching row count — the list serves the newest 200. */
  total?: number
  /** Decision 162: per-status aggregates over ALL rows — tiles read these, never the capped list. */
  statusAgg?: Record<string, { n: number; valueSell: number }>
  /** Decision 204: the same, new-business quotes only (no renewal seq or link). */
  statusAggNewBusiness?: Record<string, { n: number; valueSell: number }>
  /** Decision 166: the Active Contracts pipeline — stage-filter options only, never a board. */
  contractPipeline?: { id: string; label: string; stages: Array<{ id: string; label: string }> } | null
  partnerId: string
  quotes: QuoteRow[]
  stats?: QuoteStats
  users: Array<{ id: string; name: string }>
  /** Decision 100: the signed-in person's People-row id — null for key sessions. */
  meId?: string | null
  /** Decision 100: "needs attention" thresholds (Organisation settings). */
  nudgeDays?: { unviewed: number; expiring: number; openHorizon?: number }
  /** Decisions 142/146: the mapped pipelines — staff sessions only, [] otherwise. */
  dealPipelines?: DealPipeline[]
  /** Decision 145: stage labels an admin hid from the board — staff only. */
  boardHiddenStages?: string[]
}

/** Decision 200: the list's server-side sort — absent = newest created first. */
export type QuoteListSort = 'sent_desc' | 'sent_asc' | 'accepted_desc' | 'accepted_asc'

/** Decision 200: recipient (email or domain) filter + sort, both applied BEFORE the row window. */
export interface QuoteListOpts {
  recipient?: string
  sort?: QuoteListSort | null
  /** Decision 201: activity stats exclude renewal quotes (the list itself is unaffected). */
  newBusiness?: boolean
  /** Decision 232: the list's own filter — new business only, renewals only, or everything (SQL-side, before the window). */
  business?: QuoteBusiness
}
export type QuoteBusiness = 'all' | 'new' | 'renewal'

export const fetchQuotes = (status?: string, stage?: string, opts: QuoteListOpts = {}) => {
  const params = new URLSearchParams()
  if (status && status !== 'all') params.set('status', status)
  if (stage && stage !== 'all') params.set('stage', stage)   // decision 163: HS deal-stage filter, SQL-side
  if (opts.recipient?.trim()) params.set('recipient', opts.recipient.trim())   // decision 200
  if (opts.sort) params.set('sort', opts.sort)                                 // decision 200
  if (opts.newBusiness) params.set('newBusiness', '1')                          // decision 201
  if (opts.business && opts.business !== 'all') params.set('business', opts.business)   // decision 232
  const qs = params.toString()
  return get<QuotesResponse>(`/api/quotes${qs ? `?${qs}` : ''}`)
}

// ---------------------------------------------------------------- all-partners view (decision 130)

/** A row in the staff-only all-partners oversight list. */
export interface AllQuoteRow extends QuoteRow {
  partnerId: string
  partnerName: string
  partnerMode: PartnerMode
  partnerIsTest: boolean
  /** Unresolved §6.3 conflict alerts on this quote (sweep-filed since phase 4). */
  conflicts: number
  conflictKinds: string[]
}

export const fetchQuotesAll = (status?: string, opts: QuoteListOpts = {}) => {
  const params = new URLSearchParams({ scope: 'all' })
  if (status && status !== 'all') params.set('status', status)
  if (opts.recipient?.trim()) params.set('recipient', opts.recipient.trim())   // decision 200
  if (opts.sort) params.set('sort', opts.sort)                                 // decision 200
  if (opts.business && opts.business !== 'all') params.set('business', opts.business)   // decision 232
  return get<{ scope: 'all'; quotes: AllQuoteRow[]; dealPipelines?: DealPipeline[]; boardHiddenStages?: string[] }>(
    `/api/quotes?${params.toString()}`)
}

/** The same QuoteDetail the partner-scoped route serves — staff-only, cross-partner. */
export const fetchQuoteAcrossPartners = (id: string) => get<QuoteDetail>(`/api/quotes/${id}?scope=all`)

// ---------------------------------------------------------------- builder (phase 2)

export interface QuoteSite {
  id: string
  name: string
  urn: string | null
  postcode: string | null
  address: Record<string, unknown>
  contact: string | null
  /** Decision 153: GIAS number on roll, or null (manual site / Hub not returning it yet). */
  numberOnRoll: number | null
  sort: number
}

export interface QuoteGroupRow {
  id: string
  name: string
  sort: number
  kind: 'section' | 'choose_one'
  siteId: string | null
  /** Decision 69: choose_one blocks nest inside a section. null for sections (and legacy top-level blocks). */
  parentGroupId: string | null
  /** Decision 87: the customer can include/exclude a whole optional section. */
  optional: boolean
  /** Optional sections: whether the section STARTS included. */
  preselected: boolean
  /** Decision 117: sections sharing a key are customer alternatives (pick ONE). */
  choiceSet: string | null
  /** Decision 119: pricing presented as one-off + per-year section subtotals only. */
  subtotalOnly: boolean
}

export interface QuoteItemRow {
  id: string
  groupId: string | null
  hsProductId: string | null
  name: string
  description: string | null
  qty: number
  qtyEditable: boolean
  qtyMin: number | null
  qtyMax: number | null
  subjectToSurvey: boolean
  isAdhoc: boolean
  listPrice: number
  /** null for agent sessions — agents never see buy prices. */
  buyPrice: number | null
  sellPrice: number
  recurring: boolean
  billingFrequency?: BillingFrequency
  termMonths: number | null
  optionality: 'required' | 'optional' | 'option'
  preselected: boolean
  sort: number
  /** Decision 97: cost override state — varied-at set on any internal cost edit. */
  costVariedAt: string | null
  /** The covering log entry; null while varied-at is set = UNLOGGED (the builder nags). */
  costVariationId: string | null
}

/** Decision 97: one log entry, possibly many lines. */
export interface CostVariationRow {
  id: string
  description: string
  evidenceFilename: string | null
  createdBy: string
  createdAt: string
  /** Decision 159: true = logged on a quote this one supersedes — the varied
   *  cost carried forward, so the entry shows here with its origin named. */
  inherited?: boolean
  loggedOnNumber?: string
  lines: Array<{ lineName: string; oldCost: number; newCost: number }>
}

export interface CostVariationLogRow extends CostVariationRow {
  quoteId: string
  quoteNumber: string
  /** Decision 159: the LIVE quote now carrying these costs, when the logged-on
   *  quote has been superseded (chain resolved to its tip). Null = still live. */
  currentQuoteId?: string | null
  currentQuoteNumber?: string | null
  currentQuoteStatus?: string | null
  quoteTitle: string
  quoteStatus: string
  partnerName: string
}

export const fetchCostVariations = (quoteId?: string) =>
  get<{ variations: CostVariationLogRow[] }>(`/api/cost-variations${quoteId ? `?quote=${quoteId}` : ''}`)

export const logCostVariation = (p: {
  quoteId: string
  description: string
  items: Array<{ itemId: string; newCost: number }>
  file?: File | null
}) => {
  const form = new FormData()
  form.set('quoteId', p.quoteId)
  form.set('description', p.description)
  form.set('items', JSON.stringify(p.items))
  if (p.file) form.set('file', p.file)
  return sendMultipart<{ id: string; createdAt: string; createdBy: string; lines: Array<{ name: string; oldCost: number; newCost: number }> }>(
    '/api/cost-variations', form,
  )
}

export const costEvidenceUrl = (variationId: string) => `/api/cost-variations/${variationId}/evidence`
/** Decision 211: the original distributor workbook. */
export const supplierQuoteFileUrl = (id: string) => `/api/supplier-quotes/${id}/file`

// --- Agent claimable statements (decision 183) ------------------------------
// The row/statement shapes are the rule book's, imported rather than restated
// so the page and the server can never disagree about a statement's shape.
export interface CommissionPartnerBlock {
  partnerId: string
  partnerName: string
  totals: ClaimTotals
  statements: Array<Statement>
}

export const fetchCommissions = (partnerId?: string) =>
  get<{
    agent: boolean
    staff?: boolean
    partners: CommissionPartnerBlock[]
    totals: ClaimTotals
    /** Decision 190: the read is capped — when true, the totals shown are lower than the ledger holds. */
    truncated?: boolean
    cap?: number
  }>(`/api/commissions${partnerId ? `?partner=${partnerId}` : ''}`)

export const fetchCommissionEvents = (ledgerId: string) =>
  get<{ events: Array<{ kind: string; actor: string | null; detail: unknown; at: string }> }>(
    `/api/commissions/${ledgerId}/events`,
  )

export const claimCommissions = (p: { agentInvoiceRef?: string; partnerId?: string }) =>
  send<{ claimId: string; count: number; total: number }>('/api/commissions/claim', 'POST', p)

export const markCommissionsPaid = (claimId: string) =>
  send<{ claimId: string; count: number; status: string }>('/api/commissions/paid', 'POST', { claimId })

export const reopenCommissions = (claimId: string, reason: string) =>
  send<{ claimId: string | null; count: number; status: string }>('/api/commissions/reopen', 'POST', { claimId, reason })


export interface QuoteRecipient {
  id: string
  name: string
  email: string
  hsContactId: string | null
  createInHubspot: boolean
}

export interface QuoteAttachment {
  assetId: string
  version: number
  sort: number
  /** Decision 180: primary order key (0–99, default 50) — the list + PDF pack order. */
  priority: number
  title: string
  filename: string
  category: string | null
  /** Decision 157: a one-off uploaded straight onto this quote — never in the library. */
  adhoc: boolean
}

export interface QuoteTotals {
  oneOffSell: number
  annualSell: number
  contractValueSell: number
  oneOffBuy: number | null
  annualBuy: number | null
  contractValueBuy: number | null
  termMonths: number
  marginAmount: number | null
  marginPct: number | null
  lineCount: number
  hasSurveyLines: boolean
}

/** One line on the expiring contract — a HubSpot line-item snapshot (#105). */
export interface ContractLine {
  name: string
  qty: number | null
  price: number | null
  term: string | null
}

/**
 * The contract a W9R quote renews. Decision 127: the full reference picture,
 * shown read-only by the builder's Contract panel and the Renewals slide-over
 * (one component, `components/ContractSummary.tsx`).
 */
export interface RenewalContract {
  id: string
  hsDealId: string
  dealName: string | null
  customerName: string | null
  contractEndDate: string | null
  contractStartDate: string | null
  /** The expiring contract's ARR (hs_arr) — feeds the #ARR snippet token. */
  hsArr: number | null
  dealStage: string | null
  dealAmount: number | null
  ownerName: string | null
  quotientLink: string | null
  newDealId: string | null
  lineItems: ContractLine[] | null
  outcome: 'open' | 'rolled_over' | 'renewed' | 'lost'
  outcomeAt: string | null
  outcomeActor: string | null
  outcomeNote: string | null
  hsMoveDone: boolean
  hsLastError: string | null
  lastSyncedAt: string | null
}

/**
 * Decision 191: what the customer actually accepted — the evidentiary record
 * (quote_acceptances), read back so the builder can show it. Sell figures only.
 */
export interface AcceptanceHistoryRow {
  version: number
  selections: Array<{ itemId: string; name: string; qty: number; unitPrice: number; recurring: boolean; billingFrequency: BillingFrequency; termMonths: number | null }>
  contractValueSell: number
  accepterName: string | null
  acceptedAt: string
  poReference: string | null
  supersededAt: string
  supersededByName: string | null
  supersededOnBehalf: boolean
}

export interface QuoteAcceptance {
  acceptedAt: string
  accepterName: string | null
  accepterEmail: string | null
  poReference: string | null
  poToFollow: boolean
  termsVersion: string | null
  caveatVersion: string | null
  /** Decision 94: recorded by Wave9 staff on the customer's instruction. */
  onBehalf: boolean
  authorityNote: string | null
  surveyFlaggedItems: string[]
  /** Decision 218: 1 until amended. amended* = who last changed the record; history = what it replaced (newest first). */
  version: number
  amendedAt: string | null
  amendedByName: string | null
  amendedByEmail: string | null
  amendAuthorityNote: string | null
  amendNote: string | null
  history: AcceptanceHistoryRow[]
  /** What stops another amendment — a split / pack / PO derived from the record. Empty = amendable. */
  amendBlockers: Array<{ kind: 'split' | 'po'; label: string }>
  /** Decision 247: a provisioning pack WARNS (its HubSpot records are fixed by hand) rather than blocking. */
  amendWarnings: Array<{ kind: 'pack'; label: string; records: AmendPackRecord[] }>
  /** The accepted lines at their accepted quantities and customer prices. */
  selections: Array<{
    itemId: string
    name: string
    qty: number
    unitPrice: number
    recurring: boolean
    billingFrequency: BillingFrequency
    termMonths: number | null
    optionality: 'required' | 'optional' | 'option'
    subjectToSurvey: boolean
  }>
}

export interface QuoteDetail {
  quote: {
    id: string
    quoteNumber: string
    status: string
    title: string
    coverNote: string
    /** Decision 104: free-text footer shown after the contract value. */
    footerText: string
    validUntil: string | null
    eccNotice: boolean
    termsSetId: string | null
    referralFeeOverride: number | null
    ownerId: string | null
    /** Decision 156: the internal BCC copy — a Wave 9 person blind-copied on
     *  every quote email. Internal-mode quotes only; null = no copy. */
    bccUserId: string | null
    /** Decision 160: imported deals — the Quotient quote document; when imported. */
    quotientLink: string | null
    importedAt: string | null
    hsDealId: string | null
    hsDealLinked: boolean
    /** Decision 171: set = split into per-site deals (the parent deal is a zeroed shell). */
    splitAt: string | null
    /** Decision 195: how — 'value' (171) or 'sites' (£0 site deals, master untouched). */
    splitMode: SplitMode | null
    /** Decision 178: set = the provisioning pack (assets + stock tasks) was created. */
    provisioningPackAt: string | null
    /** Decision 86: the four builder-entered HubSpot fields. */
    dealDescription: string | null
    expectedBookingDate: string | null
    dealCategory: string | null
    dealSource: string | null
    dealType: string | null
    sentAt: string | null
    createdAt: string | null
    decidedAt: string | null
    version: number
    /** Decision 125: set = this row is a template editing workspace, not a quote. */
    templateWorkspaceFor: string | null
    /** Decision 240: set = a line-editing session is open on this draft (snapshot on file). */
    editSnapshotAt: string | null
    /** Decision 241: who opened that session, and whether it is the caller's. */
    editSnapshotBy: string | null
    editSnapshotMine: boolean
  }
  /** The contract deal this quote renews — set on W9R quotes only. */
  /**
   * The contract this quote renews (W9R quotes only). Decision 127 widened it
   * from the chip's three fields to the whole reference picture — including
   * the CURRENT CONTRACT LINES — so the builder can show it read-only.
   * ⚠️ Reference only: nothing here is ever copied into the quote.
   */
  renewal: RenewalContract | null
  /** Decision 211: the distributor quote this was imported from (internal quotes only; null otherwise). */
  supplierQuote: {
    id: string; distributor: string; reference: string
    priceProtectionUntil: string | null; priceProtectionText: string | null; filename: string; marginPct: number
    /** Decision 213: the Xero draft PO raised for it (null until raised). */
    po: { number: string | null; status: string; url: string; raisedAt: string; updatedAt: string } | null
  } | null
  /** Decision 171: the child site deals this quote was split into ([] until split). */
  splits: QuoteSplitDeal[]
  /** Decision 178: the frozen provisioning-pack record ([] until packed). */
  provisioningPack: PackRecordRow[]
  /** Decision 236: ad hoc Xero POs raised from the accepted record (internal quotes; [] otherwise). */
  adhocPurchaseOrders?: AdhocPoChip[]
  /** Decision 191: the frozen acceptance record — null until accepted. */
  acceptance: QuoteAcceptance | null
  partner: {
    id: string
    name: string
    mode: PartnerMode
    referralFee: number | null
    branding: Record<string, unknown>
    isTest: boolean
  }
  /** Decision 83: the quote owner — the "Prepared by" line on customer documents. */
  owner: { name: string | null; email: string } | null
  /** Decision 118: the address quote email actually goes from (89/90 resolution). */
  mailFrom: string
  customer: {
    id: string
    name: string
    urn: string | null
    postcode: string | null
    contactName: string | null
    contactEmail: string | null
  } | null
  sites: QuoteSite[]
  groups: QuoteGroupRow[]
  items: QuoteItemRow[]
  recipients: QuoteRecipient[]
  attachments: QuoteAttachment[]
  staleLineIds: string[]
  stalePriceIds: string[]
  staleDescriptionIds: string[]
  totals: QuoteTotals
  /** Decision 97: this quote's cost-variation log (internal quotes only in practice). */
  costVariations: CostVariationRow[]
  /**
   * Decision 128: snippet tokens still literal in the cover note / footer that
   * this quote's data cannot fill. Send expands everything it can; these would
   * otherwise reach the customer as "#FirstName". The send panel warns by name.
   */
  unresolvedTokens: string[]
}

export const createQuote = () => send<{ id: string; quoteNumber: string }>('/api/quotes', 'POST', {})

// ------------------------------------------------ templates (decisions 17/96)

export interface TemplateRow {
  id: string
  /** null = Wave9-global (visible to every partner). */
  partnerId: string | null
  name: string
  active: boolean
  createdAt: string
  summary: string
  adhocLines: number
  /** Decision 124: the presentation fields a quote made from this arrives with. */
  carries: TemplateCarries
  /** Decision 125: an unfinished editing session — offer to resume it. */
  editing: { quoteId: string; updatedAt: string | null } | null
  canManage: boolean
}

export interface TemplateCarries {
  coverNote: boolean
  footer: boolean
  terms: boolean
  attachments: number
  label: string
}

export const fetchTemplates = () => get<{ templates: TemplateRow[] }>('/api/templates')

/**
 * Decision 122: pass `templateId` to save back OVER an existing template (scope never flips).
 * Decision 124: coverNote / footerText / termsSetId are the TEMPLATE's copies —
 * sending a key overrides the source quote's value, omitting it keeps it.
 */
export const saveQuoteTemplate = (p: {
  quoteId: string; name: string; global?: boolean; templateId?: string
  coverNote?: string | null; footerText?: string | null; termsSetId?: string | null
}) =>
  send<{
    id: string; name: string; global: boolean; updated?: boolean; summary: string
    adhocLines: number; carries: TemplateCarries
  }>('/api/templates', 'POST', p)

export const patchTemplate = (id: string, patch: { name?: string; active?: boolean }) =>
  send<{ ok: true }>(`/api/templates/${id}`, 'PATCH', patch)

export const deleteTemplate = (id: string) =>
  send<{ ok: true }>(`/api/templates/${id}`, 'DELETE')

// ---------------------------------------------- template editor (decision 125)
// A template is edited in its own editor, backed by a workspace quote row that
// never enters the quote series or any list. Open → edit with the ordinary
// builder routes → save back over the template (or discard).

export interface TemplateWorkspace {
  quoteId: string
  /** true = picked up an unfinished edit rather than starting fresh. */
  resumed: boolean
  updatedAt: string | null
  name: string
  retiredTerms: string | null
}

export const openTemplateWorkspace = (templateId: string) =>
  send<TemplateWorkspace>(`/api/templates/${templateId}/workspace`, 'POST')

export const saveTemplateFromWorkspace = (templateId: string, name?: string) =>
  send<{ ok: true; id: string; name: string; summary: string; adhocLines: number; carries: TemplateCarries }>(
    `/api/templates/${templateId}/save`, 'POST', name ? { name } : {},
  )

export const discardTemplateWorkspace = (templateId: string) =>
  send<{ ok: true }>(`/api/templates/${templateId}/workspace`, 'DELETE')

/** Decision 96: structure copied, catalogue lines REPRICED from today, ad-hoc carried + flagged. */
export const createQuoteFromTemplate = (templateId: string) =>
  send<{
    id: string; quoteNumber: string; skipped: string[]; adhocCarried: string[]; templateName: string
    /** Decision 124: the template's T&Cs were retired — the quote has none. */
    retiredTerms: string | null
  }>('/api/quotes', 'POST', { templateId })
export const fetchQuote = (id: string) => get<QuoteDetail>(`/api/quotes/${id}`)

/**
 * Decision 126: batch-delete drafts from the quotes list. Drafts only —
 * anything else in `ids` is left alone and counted back as `skipped`.
 */
export const bulkDeleteDraftQuotes = (ids: string[]) =>
  send<{ ok: true; deleted: string[]; skipped: number }>('/api/quotes/bulk-delete', 'POST', { ids })

/** Decision 86: HubSpot-owned dropdown options + the portal id for deal links. */
export interface HsOptions {
  portalId: string | null
  dealCategory: { label: string | null; options: Array<{ value: string; label: string }> }
  dealSource: { label: string | null; options: Array<{ value: string; label: string }> }
  /** Decision 89: HubSpot's own Deal Type radio. */
  dealType: { label: string | null; options: Array<{ value: string; label: string }> }
  configured: boolean
}
export const fetchHsOptions = () => get<HsOptions>('/api/hs-options')

/** The deal's page in the Wave9 Hub — null when the portal id isn't known yet. */
export const hubspotDealUrl = (portalId: string | null, dealId: string | null): string | null =>
  portalId && dealId ? `https://app.hubspot.com/contacts/${portalId}/record/0-3/${dealId}` : null

// ---------------------------------------------------------------- renewals

/** One contract up for renewal (the renewals table — staff only). */
export interface RenewalRow {
  id: string
  hsDealId: string
  dealName: string | null
  customerName: string | null
  contractStartDate: string | null
  contractEndDate: string | null
  dealStage: string | null
  dealAmount: number | null
  /** The original (Quotient-era) quote — "Link or Ref", so not always a URL. */
  quotientLink: string | null
  /** HubSpot's computed ARR on the contract deal. */
  hsArr: number | null
  /** The contract deal's owner, resolved via the HubSpot-users roster. */
  ownerName: string | null
  lineItems: Array<{ name: string; qty: number | null; price: number | null; term: string | null }> | null
  outcome: 'open' | 'rolled_over' | 'renewed' | 'lost'
  outcomeAt: string | null
  outcomeActor: string | null
  outcomeNote: string | null
  quote: { id: string; number: string; status: string; hsDealId: string | null } | null
  newDealId: string | null
  hsMoveDone: boolean
  hsLastError: string | null
  lastSyncedAt: string | null
}

export interface RenewalsResponse {
  windowDays: number
  /** The At Risk stage id — the cockpit spotlight matches rows against it (112). */
  stageAtRisk: string | null
  notifyEmail: string | null
  lostReasons: Array<{ key: string; label: string }>
  renewals: RenewalRow[]
}

export const fetchRenewals = () => get<RenewalsResponse>('/api/renewals')

export interface RenewalDealHit {
  hsDealId: string
  name: string
  stage: string | null
  /** Human stage label (from the live pipelines) — falls back to the raw id. */
  stageLabel: string | null
  owner: string | null
  amount: number | null
}
export const searchRenewalDeals = (q: string) =>
  get<{ deals: RenewalDealHit[] }>(`/api/renewals/deal-search?q=${encodeURIComponent(q)}`)

/** Decision 188: a live portal quote the renewal row can adopt. */
export interface RenewalQuoteHit {
  id: string
  number: string
  title: string | null
  status: string
  partnerName: string
  customerName: string | null
  hsDealId: string | null
  sentAt: string | null
  acceptedAt: string | null
  /** Contract value (sell) — the accepted figure when there is one. */
  value: number | null
}
export const searchRenewalQuotes = (q: string) =>
  get<{ quotes: RenewalQuoteHit[] }>(`/api/renewals/quote-search?q=${encodeURIComponent(q)}`)

/** Decision 188: adopt a quote made outside the renewals page. An accepted quote closes the loop at once. */
export const linkRenewalQuote = (id: string, quoteId: string) =>
  send<{ quoteId: string; number: string; status: string; renewed: boolean; synced: boolean; dealTypeSet: boolean }>(
    `/api/renewals/${id}/link-quote`, 'POST', { quoteId },
  )

/** Decision 124: a renewal quote may start from a template (blank when omitted). */
export interface RenewalQuoteResult {
  id: string
  quoteNumber: string
  templateName?: string
  skipped?: string[]
  adhocCarried?: string[]
  retiredTerms?: string | null
}

export const createRenewalQuote = (id: string, templateId?: string | null) =>
  send<RenewalQuoteResult>(`/api/renewals/${id}/create-quote`, 'POST', templateId ? { templateId } : {})

/** Plain link tracks the deal; createQuote also mints the W9R quote ATTACHED to it. */
export const linkRenewalDeal = (id: string, hsDealId: string, createQuote = false, templateId?: string | null) =>
  send<{ dealId?: string; dealName?: string | null; associationStamped?: boolean } & Partial<RenewalQuoteResult>>(
    `/api/renewals/${id}/link-deal`, 'POST',
    { hsDealId, ...(createQuote ? { createQuote: true } : {}), ...(createQuote && templateId ? { templateId } : {}) },
  )

export const recordRenewalRollover = (id: string, body: { note?: string; newEndDate?: string }) =>
  send<{ moved: boolean; emailSent: boolean; emailError?: string }>(`/api/renewals/${id}/rollover`, 'POST', body)

export const recordRenewalLost = (id: string, body: { reason: string; detail?: string }) =>
  send<{ moved: boolean; quoteWithdrawn: boolean }>(`/api/renewals/${id}/lost`, 'POST', body)

export const markRenewalRenewedApi = (id: string) =>
  send<{ moved: boolean }>(`/api/renewals/${id}/mark-renewed`, 'POST')

export const reopenRenewal = (id: string) =>
  send<{ note?: string }>(`/api/renewals/${id}/reopen`, 'POST')

/** Portal-only working notes on a renewal row — never synced anywhere. */
export const addRenewalNote = (id: string, text: string) =>
  send<void>(`/api/renewals/${id}/note`, 'POST', { text })

/** "Renewal At Risk" — stage move + a REQUIRED note saying why. */
export const markRenewalAtRisk = (id: string, note: string) =>
  send<{ moved: boolean; moveError?: string }>(`/api/renewals/${id}/at-risk`, 'POST', { note })

export interface RenewalEvent { kind: string; actor: string | null; detail: Record<string, unknown>; at: string }
export const fetchRenewalEvents = (id: string) => get<{ events: RenewalEvent[] }>(`/api/renewals/${id}/events`)
export const patchQuote = (id: string, patch: Record<string, unknown>) =>
  send<void>(`/api/quotes/${id}`, 'PATCH', patch)

export const setQuoteCustomer = (id: string, payload: {
  name: string; urn?: string | null; postcode?: string | null; address?: unknown
  contactName?: string | null; contactEmail?: string | null
  sites: Array<{ name: string; urn?: string | null; postcode?: string | null; address?: unknown; contact?: string | null; numberOnRoll?: number | null }>
}) => send<{ customerId: string }>(`/api/quotes/${id}/customer`, 'POST', payload)

export const addQuoteGroup = (id: string, payload: { kind?: 'section' | 'choose_one'; name?: string; cloneOf?: string; parentGroupId?: string }) =>
  send<{ id: string }>(`/api/quotes/${id}/groups`, 'POST', payload)
/** Decision 118: mint a fresh live link for a recipient (no email, no sent-date bump). */
export const mintRecipientLink = (quoteId: string, recipientId: string) =>
  send<{ url: string }>(`/api/quotes/${quoteId}/recipient-link`, 'POST', { recipientId })
export const patchQuoteGroup = (id: string, patch: { id: string; name?: string; sort?: number; siteId?: string | null; optional?: boolean; preselected?: boolean; choiceSet?: string | null; subtotalOnly?: boolean }) =>
  send<void>(`/api/quotes/${id}/groups`, 'PATCH', patch)
export const deleteQuoteGroup = (id: string, groupId: string) =>
  send<void>(`/api/quotes/${id}/groups?id=${encodeURIComponent(groupId)}`, 'DELETE')

export const addQuoteItem = (id: string, payload: Record<string, unknown>) =>
  send<{ id: string; totals: QuoteTotals }>(`/api/quotes/${id}/items`, 'POST', payload)
export const patchQuoteItem = (id: string, patch: Record<string, unknown>) =>
  send<{ totals: QuoteTotals }>(`/api/quotes/${id}/items`, 'PATCH', patch)
export const deleteQuoteItem = (id: string, itemId: string) =>
  send<{ totals: QuoteTotals }>(`/api/quotes/${id}/items?id=${encodeURIComponent(itemId)}`, 'DELETE')

export const addQuoteRecipient = (id: string, payload: { name: string; email: string; hsContactId?: string | null }) =>
  send<{ id: string }>(`/api/quotes/${id}/recipients`, 'POST', payload)
export const deleteQuoteRecipient = (id: string, recipientId: string) =>
  send<void>(`/api/quotes/${id}/recipients?id=${encodeURIComponent(recipientId)}`, 'DELETE')

/**
 * Decision 157: upload an ad-hoc attachment straight onto the quote.
 * scope 'quote' (default) = one-off, invisible to the library; 'library' also
 * files it in the library (staff -> global, reseller -> their own; agents 403).
 */
export async function uploadQuoteAttachment(
  id: string, file: File, title: string, scope: 'quote' | 'library' = 'quote',
): Promise<{ ok: true; assetId: string; title: string; filename: string; scope: string }> {
  const form = new FormData()
  form.append('file', file)
  form.append('title', title)
  form.append('scope', scope)
  const res = await fetch(`/api/quotes/${id}/attachments`, { method: 'POST', body: form })
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) throw new Error(String(body.error ?? `Upload failed (${res.status})`))
  return body as { ok: true; assetId: string; title: string; filename: string; scope: string }
}

export const addQuoteAttachment = (id: string, assetId: string) =>
  send<void>(`/api/quotes/${id}/attachments`, 'POST', { assetId })
export const deleteQuoteAttachment = (id: string, assetId: string) =>
  send<void>(`/api/quotes/${id}/attachments?assetId=${encodeURIComponent(assetId)}`, 'DELETE')
/** Decision 180: reorder — a direct 0–99 priority, or an up/down nudge that swaps display neighbours. */
export const updateQuoteAttachment = (id: string, body: { assetId: string; priority?: number; move?: 'up' | 'down' }) =>
  send<void>(`/api/quotes/${id}/attachments`, 'PATCH', body)

// ---------------------------------------------------------------- phase 3 (decisions 70–76)

/**
 * Decision 90: a delegated (send-as-the-owner) failure, classified server-side.
 * `selfServe` distinguishes "the user can fix this by signing out/in" from
 * "an admin has to act"; `guidance` is the exact sentence to show them.
 */
export interface MailFailure {
  email: string
  cause: string
  selfServe: boolean
  guidance: string
}

export interface SendResult {
  ok: boolean
  links: Array<{ recipientId: string; name: string | null; email: string; url: string }>
  mail: { configured: boolean; sent: number; queued: number; failures?: MailFailure[]; skipped?: boolean }
  termsVersion: string | null
}

/** Draft → sent (decision 72: links always come back for copy-paste delivery).
 *  Decision 95: `withoutEmail` is "Mark as sent" — everything but the email. */
export const sendQuote = (id: string, opts?: { withoutEmail?: boolean }) =>
  send<SendResult>(`/api/quotes/${id}/send`, 'POST', opts?.withoutEmail ? { withoutEmail: true } : {})

/** Decision 94: staff record an acceptance given by phone / email / PO. */
export interface AcceptOnBehalfResult {
  ok: boolean
  status: 'accepted'
  contactName: string
  contactEmail: string
  link: string | null
  confirmation: { sent: boolean; queued: boolean } | null
  contractValueSell: number
  lines: number
}

export const acceptQuoteOnBehalf = (id: string, p: {
  recipientId?: string
  contactName?: string
  contactEmail?: string
  poReference?: string
  poToFollow?: boolean
  note?: string
  selections?: Record<string, { selected?: boolean; qty?: number }>
  sendConfirmation?: boolean
  confirmed: boolean
}) => send<AcceptOnBehalfResult>(`/api/quotes/${id}/accept-on-behalf`, 'POST', p)

/** Decision 218: the customer's single-use amend link (the accepter's by default) — copy-and-paste, no email. */
export const mintAmendLink = (id: string, recipientId?: string) =>
  send<{ url: string; recipient: { id: string; name: string | null; email: string } }>(`/api/quotes/${id}/amend-link`, 'POST', recipientId ? { recipientId } : {})

export interface AmendPackRecord { kind: 'asset' | 'task'; hsObjectId: string; itemId: string | null; name: string; qty: number | null }

export interface AmendAcceptanceResult {
  ok: true
  version: number
  previousVersion: number
  contactName: string
  contractValueSell: number
  lines: number
  change: { added: string[]; removed: string[]; qtyChanged: string[] }
  /** Decision 247: the provisioning-pack warning(s) and the records whose line this amendment changed. */
  warnings: Array<{ kind: 'pack'; label: string; records: AmendPackRecord[] }>
  affectedPackRecords: AmendPackRecord[]
}

/** Decision 218: staff record an amendment on the customer's instruction — the accept-on-behalf idiom. */
export const amendAcceptanceOnBehalf = (id: string, p: {
  recipientId?: string
  contactName?: string
  contactEmail?: string
  poReference?: string
  note?: string
  expectedVersion?: number
  selections?: Record<string, { selected?: boolean; qty?: number }>
  confirmed: boolean
}) => send<AmendAcceptanceResult>(`/api/quotes/${id}/amend-acceptance`, 'POST', p)

/** Decision 121: staff record a decline the customer gave by phone/email —
 *  silent (no customer email), reason from the shared picklist. */
export const declineQuoteOnBehalf = (id: string, p: { reasonKey: string; detail?: string }) =>
  send<{ ok: true }>(`/api/quotes/${id}/decline-on-behalf`, 'POST', p)

/** Clone a sent/decided quote into a new editable draft, v+1 (decision 73). */
export const supersedeQuote = (id: string) =>
  send<{ id: string; quoteNumber: string; existing?: boolean }>(`/api/quotes/${id}/supersede`, 'POST', {})

/** Watermarked, non-binding preview link on a draft (decision 75). */
export const createPreviewLink = (id: string) =>
  send<{ url: string }>(`/api/quotes/${id}/preview-link`, 'POST', {})

// ------------------------------------------------- round 4 (decisions 78–81)

/** Fresh link(s) + email — one recipient or all; bumps the last-sent date (decision 78). */
export const resendQuote = (id: string, recipientId?: string) =>
  send<SendResult>(`/api/quotes/${id}/resend`, 'POST', recipientId ? { recipientId } : {})

/** Pull a sent quote before decision (decision 78); revive by supersede. */
export const withdrawQuote = (id: string, note?: string) =>
  send<void>(`/api/quotes/${id}/withdraw`, 'POST', { note })

/**
 * Decision 208: a later valid-until on a sent / viewed / EXPIRED quote — no new
 * version, no resend, the customer's links carry on. An expired quote comes
 * back to sent/viewed and its deal is reconciled.
 */
export const extendQuoteValidity = (id: string, validUntil: string) =>
  send<{ ok: true; validUntil: string; status: string; reinstated: boolean }>(`/api/quotes/${id}/extend`, 'POST', { validUntil })

/**
 * Decision 240: the line editor's session on a DRAFT. `open` stores a snapshot
 * of the lines (or reports an unfinished session to resume), `save` keeps the
 * edits and clears it, `discard` puts the lines back as they were at open.
 */
export const quoteEditor = (id: string, action: 'open' | 'save' | 'discard') =>
  send<{ ok: true; resumed?: boolean; snapshotAt?: string; by?: string | null; mine?: boolean; restored?: { groups: number; items: number } }>(`/api/quotes/${id}/editor`, 'POST', { action })

/** The date Extend proposes: today + the standard validity, as yyyy-mm-dd. */
export function proposedExtension(days = 30): string {
  const d = new Date(); d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

/** One prompt for every Extend button (builder, list, cockpit): returns the date or null. */
export function askExtensionDate(quoteNumber: string, staleLines = 0): string | null {
  const stale = staleLines > 0
    ? `\n\n⚠ ${staleLines} line${staleLines === 1 ? ' has' : 's have'} moved in the catalogue since this quote was priced — the customer still sees the ORIGINAL prices. Supersede instead if they should be re-priced.`
    : ''
  const v = window.prompt(`Extend ${quoteNumber} — the customer's existing links keep working, nothing is emailed (use Links / resend to tell them).${stale}\n\nNew valid-until date (yyyy-mm-dd):`, proposedExtension())
  if (v === null) return null
  const t = v.trim()
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null
}

/** DRAFTS ONLY — full delete (decision 78). */
export const deleteQuoteFully = (id: string) =>
  send<{ deleted: string }>(`/api/quotes/${id}`, 'DELETE')

/** Clone any quote as a fresh draft, REPRICED from today's catalogue (decision 80). */
export const duplicateQuote = (id: string) =>
  send<{ id: string; quoteNumber: string; skipped: string[] }>(`/api/quotes/${id}/duplicate`, 'POST', {})

// ------------------------------------------------- per-site deal split (decision 171)

/** One section→site assignment on the split screen ('shared' = the shared bucket). */
export interface SplitMappingEntry { groupId: string; target: string }

export interface SplitPlanDeal {
  suffix: string
  /** null on a phase deal (216). */
  siteId: string | null
  siteName: string
  siteUrn: string | null
  carriesShared: boolean
  dealName: string
  quoteRef: string
  amount: number
  /** Decision 195: what the site's lines are worth on the master — in sites mode `amount` is 0 and this is what the preview shows. */
  sliceAmount: number
  sellTotal: number | null
  company: { id: string | null; source: 'site' | 'trust' }
  lines: Array<{ name: string; qty: number; amount: number }>
  groupIds: string[]
  /** Decision 216: set on phase deals. */
  phaseKey?: string | null
  sectionNames?: string[]
  expectedBookingDate?: string | null
}

/** Decision 195: value = children carry the money (171); sites = £0 site deals, master untouched. Decision 216: phases = the money by section/phase, own booking dates, master zeroed. */
export type SplitMode = 'value' | 'sites' | 'phases'

/** Decision 216: one planned phase on the split screen. */
export interface SplitPlanPhase {
  key: string
  suffix: string
  name: string
  sectionNames: string[]
  groupIds: string[]
  expectedBookingDate: string | null
  isRemainder: boolean
}

export interface SplitPlan {
  dryRun: true
  /** false = the current mapping is incomplete/invalid — `error` says why; `deals` is empty. */
  valid: boolean
  error?: string
  sections: Array<{ id: string; name: string; siteId: string | null; lineCount: number; amount: number }>
  sites: Array<{ id: string; name: string; urn: string | null; postcode: string | null }>
  mapping: SplitMappingEntry[]
  defaultMapping: SplitMappingEntry[]
  sharedSiteId: string | null
  mode: SplitMode
  /** Decision 216: the planned phases (empty unless mode = phases) and the dates the client sent. */
  phases?: SplitPlanPhase[]
  phaseDates?: Record<string, string | null>
  masterBookingDate?: string | null
  parent: {
    dealId: string; stage: string | null; amount: number | null
    /** What the master looks like after the commit (absent on an invalid-mapping seed). */
    after?: { amount: number | null; stage: string | null }
  }
  splitStageSet: boolean
  /** false in sites mode — no shell stage is needed. */
  splitStageRequired?: boolean
  deals: SplitPlanDeal[]
  totalAmount: number
  warnings: string[]
}

export interface SplitCommitResult {
  ok: true
  mode: SplitMode
  deals: Array<{ suffix: string; dealId: string; siteName: string; amount: number; resumed: boolean }>
  parent: { dealId: string; stage: string | null; amount: number | null }
  warnings: string[]
}

/** A child deal on an already-split quote (the builder's split panel). */
export interface QuoteSplitDeal {
  mode: SplitMode
  suffix: string
  siteName: string
  siteUrn: string | null
  hsDealId: string
  companySource: 'site' | 'trust'
  carriesShared: boolean
  amount: number
  sellTotal: number | null
  createdAt: string | null
  /** Decision 216: set on phase children. */
  phaseKey?: string | null
  expectedBookingDate?: string | null
}

/**
 * Decision 171: split an accepted internal quote into one HubSpot deal per
 * site. Propose-first: omit `confirmed` for the dry-run plan (real numbers,
 * nothing created); send the same mapping with `confirmed: true` to commit.
 */
export const splitQuoteDeals = (id: string, p: {
  mode?: SplitMode
  mapping?: SplitMappingEntry[]
  sharedSiteId?: string | null
  /** Decision 216: per-phase expected booking dates (yyyy-mm-dd or null = the master's). */
  phaseDates?: Record<string, string | null>
  confirmed?: boolean
}) => send<SplitPlan | SplitCommitResult>(`/api/quotes/${id}/split-deals`, 'POST', p)

// --- provisioning pack (decision 178) ---------------------------------------

export interface PackRow {
  itemId: string
  dealId: string
  dealRef: string
  siteName: string
  sitePostcode: string | null
  siteUrn: string | null
  productName: string
  sku: string | null
  qty: number
  serviceType: string | null
  pipelineId: string | null
  pipelineLabel: string | null
  line: string | null
  unrouted: boolean
  uniqueRef: string
  description: string
  unitAnnualCost: number
  termMonths: number | null
  supplier: string | null
  supplierOptions: string[]
  costTrail: {
    lineName: string
    description: string
    oldCost: number | null
    newCost: number | null
    loggedByName: string
    loggedAt: string
    evidenceFilename: string | null
  } | null
  stockMatches: number | null
  existingAsset: string | null
  existingTask: string | null
}

export interface PackPlan {
  dryRun: true
  targets: Array<{ dealId: string; dealRef: string; siteName: string }>
  split: boolean
  rows: PackRow[]
  serviceTypeOptions: string[]
  targetDate: string | null
  warnings: string[]
}

export interface PackCommitResult {
  ok: true
  assets: Array<{ kind: 'asset'; itemId: string; dealId: string; hsObjectId: string; name: string; resumed: boolean }>
  stockTasks: Array<{ kind: 'task'; itemId: string; dealId: string; hsObjectId: string; name: string; resumed: boolean }>
  costNotes: number
  warnings: string[]
}

export interface PackDecisionEntry {
  itemId: string
  dealId: string
  include: boolean
  source: 'new' | 'stock'
  supplier?: string | null
}

/** The frozen pack record on the detail payload (deal_assets rows). */
export interface PackRecordRow {
  dealId: string
  itemId: string | null
  kind: 'asset' | 'task'
  hsObjectId: string
  detail: Record<string, unknown> | null
  createdAt: string | null
}

/**
 * Decision 178: the provisioning pack. Propose-first — omit `confirmed` for
 * the dry-run rows (with informational stock counts); send per-row decisions
 * with `confirmed: true` to commit. Assets, stock tasks and cost notes only —
 * projects and checklists come from the HubSpot admin's workflows.
 */
export const provisioningPack = (id: string, p: {
  decisions?: PackDecisionEntry[]
  confirmed?: boolean
}) => send<PackPlan | PackCommitResult>(`/api/quotes/${id}/provisioning-pack`, 'POST', p)

export interface QuoteEvent {
  id: string
  kind: string
  detail: Record<string, unknown>
  at: string
  recipient: { name: string | null; email: string } | null
}

/** Decision 248: what has drifted from the accepted record — the deal's line items now vs the acceptance, with #224 entries alongside. */
export interface VariationLine { name: string; sku: string | null; hsProductId: string | null; qty: number; unitPrice: number; hsLineItemId: string | null }
export interface RecordedVariation { id: string; source: 'quote' | 'billing' | 'variation_quote'; ledgerId: string | null; kind: 'added' | 'removed' | 'changed'; name: string; sku: string | null; hsProductId: string | null; hsLineItemId: string | null; reason: string; createdBy: string; createdAt: string }
export interface ContractVariationRow { kind: 'added' | 'removed' | 'changed'; accepted: VariationLine | null; onDeal: VariationLine | null; recorded: RecordedVariation[] }
export type ContractVariations =
  | { state: 'no_deal'; ledger: RecordedVariation[] }
  | { state: 'split'; mode: string; children: number; ledger: RecordedVariation[] }
  | { state: 'deal_missing'; ledger: RecordedVariation[] }
  | { state: 'ok'; readAt: string; matched: number; rows: ContractVariationRow[]; unmatchedRecorded: RecordedVariation[]; ledger: RecordedVariation[] }
export const fetchContractVariations = (id: string) => get<ContractVariations>(`/api/quotes/${id}/contract-variations`)
/** Decision 249: record the reason for a change on the quote-side ledger. */
export const recordContractVariation = (id: string, p: {
  kind: 'added' | 'removed' | 'changed'; lineName: string; sku?: string | null; hsProductId?: string | null; hsLineItemId?: string | null
  before?: { qty: number; unitPrice: number } | null; after?: { qty: number; unitPrice: number } | null; reason: string
}) => send<{ ok: true; id: string; variations: ContractVariations }>(`/api/quotes/${id}/contract-variations`, 'POST', p)

/** The activity trail (decision 81) — newest first. */
export const fetchQuoteEvents = (id: string) =>
  get<{ events: QuoteEvent[] }>(`/api/quotes/${id}/events`)

export const refreshQuotePrices = (id: string, what: 'prices' | 'descriptions' | 'both' = 'both') =>
  send<{ refreshed: number; missing: string[]; what: string; totals: QuoteTotals }>(
    `/api/quotes/${id}/refresh-prices`, 'POST', { what })

// ---------------------------------------------------------------- builder lookups

export interface SchoolHit {
  kind: 'school' | 'trust'
  name: string
  urn: string | null
  postcode: string | null
  town: string | null
  address: unknown
  la: string | null
  phase: string | null
  status: string | null
  trustUid: string | null
  trustName: string | null
  /** Trust rows only: how many open schools the roster holds. */
  openSchools: number | null
  /** Decision 153: GIAS number on roll for a school row, or null. */
  numberOnRoll: number | null
  /** Live Wave9 customer (Hub knowledge). STAFF sessions only — null for partners. */
  customer: boolean | null
  /** Decision 234: the postal address GIAS holds (each null where the Hub sends none). */
  postal?: { line1: string | null; line2: string | null; town: string | null; county: string | null; postcode: string | null; phone: string | null }
  /** Additional sites (2026-10-01): a trust site DfE/GIAS doesn't list, from the
   *  Hub's register — `urn` is its pseudo-URN (9,000,001+), shown as a site ref. */
  register?: boolean
  siteKind?: string | null
  hostName?: string | null
}

export const searchSchools = (q: string, customersOnly = false) =>
  get<{ schools: SchoolHit[]; canFilterCustomers?: boolean; attribution?: string }>(
    `/api/schools?q=${encodeURIComponent(q)}${customersOnly ? '&customers=1' : ''}`)

/** Decision 62: a trust's member schools for the "pick sites" flow. */
export const fetchTrustRoster = (trustUid: string) =>
  get<{ schools: SchoolHit[]; trust?: SchoolHit; attribution?: string }>(
    `/api/schools?trustUid=${encodeURIComponent(trustUid)}`)

export interface ContactHit {
  hsContactId: string
  name: string
  email: string | null
  company: string | null
  jobTitle: string | null
}

export const searchContacts = (q: string) =>
  get<{ contacts: ContactHit[] }>(`/api/contacts?q=${encodeURIComponent(q)}`)

export interface DealHit {
  hsDealId: string
  name: string
  stage: string | null
  amount: number | null
}

export const searchDeals = (q: string) =>
  get<{ deals: DealHit[] }>(`/api/deals?q=${encodeURIComponent(q)}`)

export interface AssetRow {
  id: string
  title: string
  filename: string
  category: string | null
  version: number
  suggested: boolean
}

export const fetchAssets = (suggestForQuoteId?: string) =>
  get<{ assets: AssetRow[] }>(`/api/assets${suggestForQuoteId ? `?suggest=${suggestForQuoteId}` : ''}`)

// ------------------------------------------------- asset library (decision 93)

export interface LibraryAsset {
  id: string
  title: string
  filename: string
  category: string | null
  hsProductIds: string[]
  /** Decision 99: auto-suggest keys on Product Lines now. */
  productLines: string[]
  version: number
  active: boolean
  uploadedAt: string | null
  uploadedBy: string | null
  sizeBytes: number | null
  /** How many quotes (any status) have this asset pinned. */
  attachedCount: number
  /** Decision 135: the owning reseller — null = Wave9-global. */
  partnerId: string | null
  partnerName: string | null
  /** Decision 135: whether THIS session may manage the row. */
  editable: boolean
}

export interface LibraryProduct {
  hsProductId: string
  name: string
  sku: string | null
}

export interface AssetLibraryResponse {
  assets: LibraryAsset[]
  products: LibraryProduct[]
  /** Decision 99: the Product Line names for the tag picker. */
  productLines: string[]
  maxBytes: number
}

export const fetchAssetLibrary = () => get<AssetLibraryResponse>('/api/assets?manage=1')

/** Multipart send — uploads must NOT set content-type; the browser adds the boundary. */
async function sendMultipart<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: 'POST', body: form })
  const body = await res.json().catch(() => ({})) as Record<string, unknown>
  if (!res.ok) throw new Error((body.error as string) || `${path} failed (${res.status})`)
  return body as T
}

// ------------------------------------------------ supplier quotes (decision 211)

export interface SupplierQuoteHeaderView {
  distributor: string
  reference: string | null
  ccwReference: string | null
  currency: string | null
  exchangeRate: number | null
  priceProtectionText: string | null
  priceProtectionUntil: string | null
  quotedOn: string | null
  salesperson: string | null
  totalExVat: number | null
}
export interface SupplierPreviewLine {
  lineRef: string
  parentLineRef: string | null
  sku: string
  description: string
  qty: number
  unitListUsd: number | null
  unitCost: number
  total: number | null
  serviceMonths: number | null
  leadTime: string | null
  status: 'matched' | 'inactive' | 'new'
  hsProductId: string | null
  catalogueName: string | null
  catalogueCost: number | null
  catalogueListPrice: number | null
  sellPrice: number
}
export interface SupplierPreview {
  header: SupplierQuoteHeaderView
  marginPct: number
  lines: SupplierPreviewLine[]
  warnings: string[]
  totals: { cost: number; sell: number; marginPct: number | null }
  productsToCreate: number
  writesDisabled: boolean
  filename: string
}
export type SupplierLineOverride = { lineRef: string; qty?: number | null; sellPrice?: number | null; include?: boolean }

export const previewSupplierQuote = (file: File, marginPct: number, overrides: SupplierLineOverride[]) => {
  const form = new FormData()
  form.set('file', file)
  form.set('marginPct', String(marginPct))
  form.set('overrides', JSON.stringify(overrides))
  return sendMultipart<SupplierPreview>('/api/supplier-quotes/preview', form)
}

export const importSupplierQuote = (
  file: File, marginPct: number, overrides: SupplierLineOverride[], reference: string,
  /** Decision 211b: start from a template; the distributor section goes first or last among its sections. */
  template?: { templateId: string; placement: 'first' | 'last' } | null,
) => {
  const form = new FormData()
  form.set('file', file)
  form.set('marginPct', String(marginPct))
  form.set('overrides', JSON.stringify(overrides))
  form.set('reference', reference)
  if (template) { form.set('templateId', template.templateId); form.set('placement', template.placement) }
  return sendMultipart<{
    quoteId: string; quoteNumber: string; supplierQuoteId: string; reference: string
    linesImported: number; productsCreated: string[]; warnings: string[]; templateName: string | null
  }>('/api/supplier-quotes', form)
}

// Decision 212: apply a distributor quote to an EXISTING draft.
export type SupplierReprice = 'keep' | 'margin'
export interface SupplierApplyLine extends SupplierPreviewLine {
  action: 'update' | 'add'
  itemId: string | null
  currentCost: number | null
  currentSell: number | null
  currentQty: number | null
  newSell: number
  newQty: number
  sectionName: string | null
}
export interface SupplierApplyPreview {
  header: SupplierQuoteHeaderView
  marginPct: number
  reprice: SupplierReprice
  lines: SupplierApplyLine[]
  missing: Array<{ itemId: string; sku: string | null; name: string; qty: number; cost: number; sectionName: string }>
  groups: Array<{ id: string; name: string }>
  warnings: string[]
  totals: { costBefore: number; costAfter: number; sellBefore: number; sellAfter: number; marginBefore: number | null; marginAfter: number | null }
  productsToCreate: number
  writesDisabled: boolean
  filename: string
}
const supplierApplyForm = (quoteId: string, file: File, marginPct: number, reprice: SupplierReprice, overrides: SupplierLineOverride[]) => {
  const form = new FormData()
  form.set('file', file)
  form.set('quoteId', quoteId)
  form.set('marginPct', String(marginPct))
  form.set('reprice', reprice)
  form.set('overrides', JSON.stringify(overrides))
  return form
}
export const previewSupplierApply = (quoteId: string, file: File, marginPct: number, reprice: SupplierReprice, overrides: SupplierLineOverride[]) =>
  sendMultipart<SupplierApplyPreview>('/api/supplier-quotes/apply/preview', supplierApplyForm(quoteId, file, marginPct, reprice, overrides))
export const applySupplierQuote = (quoteId: string, file: File, marginPct: number, reprice: SupplierReprice, overrides: SupplierLineOverride[], reference: string, groupId: string | null) => {
  const form = supplierApplyForm(quoteId, file, marginPct, reprice, overrides)
  form.set('reference', reference)
  if (groupId) form.set('groupId', groupId)
  return sendMultipart<{ quoteId: string; supplierQuoteId: string; reference: string; updated: number; added: number; missing: number; productsCreated: string[]; warnings: string[] }>('/api/supplier-quotes/apply', form)
}

export interface SupplierQuoteRow {
  id: string
  distributor: string
  reference: string
  ccwReference: string | null
  currency: string
  exchangeRate: number | null
  priceProtectionText: string | null
  priceProtectionUntil: string | null
  quotedOn: string | null
  salesperson: string | null
  totalExVat: number | null
  marginPct: number
  filename: string
  sizeBytes: number
  createdBy: string
  createdAt: string
  quoteId: string | null
  quoteNumber: string | null
  quoteTitle: string | null
  quoteStatus: string | null
  lineCount: number
  productsCreated: number
  /** Decision 213: the live Xero PO for this quote + distributor, if raised. */
  po: PurchaseOrderView | null
}
export interface SupplierQuoteLineRow {
  id: string
  lineRef: string
  parentLineRef: string | null
  sku: string
  description: string
  qty: number
  unitListUsd: number | null
  unitCost: number
  total: number | null
  serviceMonths: number | null
  leadTime: string | null
  hsProductId: string | null
  productCreated: boolean
  quoteItemId: string | null
  sellPrice: number | null
  quoteQty: number | null
  quoteSell: number | null
  quoteCost: number | null
}
export const fetchSupplierQuotes = (quoteId?: string) =>
  get<{ supplierQuotes: SupplierQuoteRow[] }>(`/api/supplier-quotes${quoteId ? `?quote=${encodeURIComponent(quoteId)}` : ''}`)
export const fetchSupplierQuote = (id: string) =>
  get<{ supplierQuote: SupplierQuoteRow; lines: SupplierQuoteLineRow[] }>(`/api/supplier-quotes/${id}`)

// ------------------------------------------------- Xero (decision 213)
export interface XeroPoSettings { contactId: string; contactName: string; accountCode: string; taxType: string }
export interface XeroStatus {
  configured: boolean
  missingVars: string[]
  connected: boolean
  tenantName: string | null
  connectedBy: string | null
  connectedAt: string | null
  refreshedAt: string | null
  lastKeepaliveAt: string | null
  lastError: string | null
  lastErrorAt: string | null
  scopesMissing: string[]
  budget: { dayRemaining: number | null; minuteRemaining: number | null; at: string | null; blockedUntil: string | null; low: boolean; floor: number; dayLimit: number }
  settings: XeroPoSettings
}
export interface XeroContact { contactId: string; name: string; email: string | null; isSupplier: boolean }
export const fetchXeroStatus = () => get<XeroStatus>('/api/xero/status')
/** Decision 233: the Xero call ledger by hour — the portal's own calls, and the drop it cannot account for. */
export interface XeroHour { hour: string; calls: number; refused: number; lowest: number | null; last: number | null; foreign: number; minuteForeign: number; minutesCrowded: number; paths: string }
/** Decision 238: `notOurs` is the level-based figure (what entered the window, less ours); `minute` is the in-minute evidence. */
export interface XeroCalls {
  hours: XeroHour[]; calls: number; foreign: number; since: string
  dayLimit: number; dayRemaining: number | null; readingAt: string | null
  spent: number | null; notOurs: number | null
  minute: { calls: number; minutesSampled: number; minutesCrowded: number; worst: { at: string; foreign: number } | null }
}
export const fetchXeroCalls = (hours = 24) => get<XeroCalls>(`/api/xero/calls?hours=${hours}`)
export const xeroConnectUrl = () => '/api/xero/connect'
export const disconnectXero = () => send<{ ok: true; status: XeroStatus }>('/api/xero/disconnect', 'POST')
export const searchXeroContacts = (q: string) => get<{ contacts: XeroContact[] }>(`/api/xero/contacts?q=${encodeURIComponent(q)}`)
export const saveXeroSettings = (patch: Partial<XeroPoSettings>) => send<{ ok: true; settings: XeroPoSettings }>('/api/xero/settings', 'PATCH', patch)
/** Decision 214: sales nominal → purchase nominal, one row per sales code in use. */
export interface NominalRow {
  salesCode: string
  purchaseCode: string | null
  note: string | null
  productCount: number
  activeCount: number
  example: string | null
  updatedAt: string | null
  updatedBy: string | null
}
export const fetchNominals = () => get<{ nominals: NominalRow[] }>('/api/xero/nominals')
export const saveNominal = (salesCode: string, purchaseCode: string, note?: string | null) =>
  send<{ ok: true; nominals: NominalRow[] }>('/api/xero/nominals', 'PATCH', { salesCode, purchaseCode, note: note ?? null })

/** The PO as the portal records it — one live PO per quote and distributor. */
export interface PurchaseOrderView {
  id: string
  xeroPoId: string
  xeroPoNumber: string | null
  xeroStatus: string
  contactName: string
  contactId?: string
  reference: string
  subTotal: number | null
  total: number | null
  lineCount: number
  quoteStatusAtRaise: string | null
  raisedByName: string
  raisedAt: string
  updatedByName: string | null
  updatedAt: string
  statusCheckedAt: string | null
  supplierQuoteId: string | null
  /** Decision 236: 'distributor' (#213) or 'adhoc'. */
  kind?: 'distributor' | 'adhoc'
  url: string
  canUpdate: boolean
}
export interface PoLine { quoteItemId: string; sku: string; description: string; qty: number; unitCost: number; lineTotal: number; salesNominal: string | null; accountCode: string | null }
export interface PoPreview {
  quoteId: string
  quoteNumber: string
  quoteTitle: string
  quoteStatus: string
  customerName: string | null
  distributor: string
  supplierReference: string
  reference: string
  lines: PoLine[]
  subTotal: number
  settings: XeroPoSettings
  connected: boolean
  configured: boolean
  existing: null | { id: string; xeroPoId: string; xeroPoNumber: string | null; xeroStatus: string; raisedAt: string; raisedByName: string; updatedAt: string; url: string; canUpdate: boolean }
  warnings: string[]
  blockers: string[]
}
export const fetchPoPreview = (supplierQuoteId: string) => get<PoPreview>(`/api/supplier-quotes/${supplierQuoteId}/po`)
export const raisePurchaseOrder = (supplierQuoteId: string) =>
  send<{ ok: true; action: 'created' | 'updated'; po: PurchaseOrderView; warnings: string[] }>(`/api/supplier-quotes/${supplierQuoteId}/po`, 'POST')
// Decision 236 — ad hoc purchase orders from an accepted quote.
export interface AdhocPoChip { id: string; number: string | null; status: string; supplier: string; url: string }
export interface AdhocOrderedOn { poId: string; poNumber: string | null; status: string; qty: number }
export interface AdhocPoLine {
  quoteItemId: string
  sku: string
  name: string
  description: string
  acceptedQty: number
  quoteUnitCost: number
  costBasis: 'one_off' | 'year'
  recurring: boolean
  termMonths: number | null
  salesNominal: string | null
  accountCode: string | null
  isAdhoc: boolean
  onOrder: AdhocOrderedOn[]
}
export interface AdhocStoredLine extends PoLine { acceptedQty?: number; quoteUnitCost?: number; edited?: boolean; costBasis?: 'one_off' | 'year' }
export type AdhocPoView = PurchaseOrderView & { lines: AdhocStoredLine[] }
export interface AdhocPreview {
  quoteId: string
  quoteNumber: string
  quoteTitle: string
  quoteStatus: string
  customerName: string | null
  lines: AdhocPoLine[]
  purchaseOrders: AdhocPoView[]
  settings: { accountCode: string; taxType: string }
  configured: boolean
  connected: boolean
  warnings: string[]
  blockers: string[]
}
export const fetchAdhocPoPreview = (quoteId: string) => get<AdhocPreview>(`/api/quotes/${quoteId}/purchase-orders`)
export const searchPoSuppliers = (quoteId: string, q: string) =>
  get<{ contacts: XeroContact[] }>(`/api/quotes/${quoteId}/purchase-orders/contacts?q=${encodeURIComponent(q)}`)
export const raiseAdhocPo = (quoteId: string, payload: {
  contactId: string; contactName: string; poId?: string | null
  lines: Array<{ quoteItemId: string; qty: number; unitCost: number }>
}) => send<{ ok: true; action: 'created' | 'updated'; po: AdhocPoView; warnings: string[]; historyNoted: boolean }>(`/api/quotes/${quoteId}/purchase-orders`, 'POST', payload)

export const refreshPurchaseOrderStatus = (supplierQuoteId: string) =>
  send<{ ok: true; po: PurchaseOrderView }>(`/api/supplier-quotes/${supplierQuoteId}/po/status`, 'POST')

export const uploadAsset = (p: { file: File; title: string; category?: string; hsProductIds?: string[]; productLines?: string[] }) => {
  const form = new FormData()
  form.set('file', p.file)
  form.set('title', p.title)
  if (p.category) form.set('category', p.category)
  if (p.hsProductIds?.length) form.set('hsProductIds', JSON.stringify(p.hsProductIds))
  if (p.productLines?.length) form.set('productLines', JSON.stringify(p.productLines))
  return sendMultipart<{ id: string; title: string; filename: string; version: number }>('/api/assets', form)
}

export const uploadAssetVersion = (assetId: string, file: File) => {
  const form = new FormData()
  form.set('file', file)
  return sendMultipart<{ id: string; filename: string; version: number }>(`/api/assets/${assetId}/version`, form)
}

export const patchLibraryAsset = (assetId: string, patch: {
  title?: string; category?: string | null; hsProductIds?: string[]; productLines?: string[]; active?: boolean
}) => send<{ ok: true }>(`/api/assets/${assetId}`, 'PATCH', patch)

// ---------------------------------------------------------------- snippets (decision 67)

export interface SnippetRow {
  id: string
  partnerId: string | null
  title: string
  body: string
  sort: number
}

/** Decision 106: a snippet placeholder — #EndDate etc. — mapped to in-app data. */
export interface SnippetTokenRow { id: string; token: string; source: string; sort: number }
export interface TokenSourceMeta { key: string; label: string; description: string }

export interface SnippetsResponse {
  editable: boolean
  snippets: SnippetRow[]
  tokens: SnippetTokenRow[]
  tokenSources: TokenSourceMeta[]
}
export const fetchSnippets = () => get<SnippetsResponse>('/api/snippets')
export const createSnippetToken = (token: string, source: string) =>
  send<{ id: string }>('/api/snippets/tokens', 'POST', { token, source })
export const patchSnippetToken = (id: string, patch: { token?: string; source?: string }) =>
  send<void>('/api/snippets/tokens', 'PATCH', { id, ...patch })
export const deleteSnippetToken = (id: string) =>
  send<void>(`/api/snippets/tokens?id=${encodeURIComponent(id)}`, 'DELETE')
export const createSnippet = (payload: { title: string; body: string; partnerId?: string | null }) =>
  send<{ id: string }>('/api/snippets', 'POST', payload)
/** Decision 113: snippets are editable in place from the Snippets page. */
export const patchSnippet = (id: string, patch: { title?: string; body?: string }) =>
  send<void>('/api/snippets', 'PATCH', { id, ...patch })
export const deleteSnippet = (id: string) =>
  send<void>(`/api/snippets?id=${encodeURIComponent(id)}`, 'DELETE')

// ---------------------------------------------------------------- terms sets (decision 63)

export interface TermsSetRow {
  id: string
  name: string
  /** Decision 133: the owning reseller — null = Wave9-global. */
  partnerId: string | null
  suggestLines: string[]
  sort: number
  active: boolean
  currentVersion: string | null
  currentUrl: string | null
  /** Decision 134: the current version's uploaded-PDF filename, if it is one. */
  currentFilename: string | null
  /** Decision 134: the current version's document — uploaded PDF path or external URL. */
  currentDocUrl: string | null
  versionCount: number
}

/**
 * Default GET scopes by the ACTING partner (decision 133) — resellers see their
 * own sets, everyone else the Wave9-global library. `scope` (admin only) is the
 * management view: 'global' or a reseller's partner id.
 */
export const fetchTermsSets = (scope?: string) =>
  get<{ editable: boolean; scope: string; sets: TermsSetRow[] }>(
    `/api/terms-sets${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`)
export const createTermsSet = (payload: { name: string; suggestLines?: string[]; partnerId?: string | null }) =>
  send<{ id: string }>('/api/terms-sets', 'POST', payload)
export const addTermsVersion = (payload: { setId: string; version: string; url?: string; content?: string }) =>
  send<{ id: string }>('/api/terms-sets', 'POST', payload)
export const patchTermsSet = (patch: { id: string; name?: string; suggestLines?: string[]; active?: boolean }) =>
  send<void>('/api/terms-sets', 'PATCH', patch)

/** Decision 134: raw-body PDF upload as a new (current) version of a set. */
export async function uploadTermsPdf(setId: string, version: string, file: File): Promise<{ docUrl: string }> {
  const qs = new URLSearchParams({ setId, version, filename: file.name })
  const res = await fetch(`/api/terms-sets/file?${qs}`, { method: 'POST', body: file })
  const body = (await res.json().catch(() => ({}))) as { docUrl?: string; error?: string }
  if (!res.ok) throw new Error(body.error ?? `Upload failed (${res.status})`)
  return { docUrl: String(body.docUrl) }
}

// ---------------------------------------------------------------- organisation (decision 66)

export interface PortalSettings {
  editable: boolean
  settings: Record<string, string>
  missing: string[]
}

export const fetchPortalSettings = () => get<PortalSettings>('/api/portal-settings')
export const savePortalSettings = (patch: Record<string, string>) =>
  send<void>('/api/portal-settings', 'PATCH', patch)

// ---------------------------------------------------------------- admin access
// ⚠️ TEMPORARY-AUTH: one shared key, exchanged for an httpOnly staff-session
// cookie by /api/admin-unlock. The credential never touches the URL or JS
// storage. Real staff accounts + RBAC replace only the unlock step.

/**
 * `user`  — Wave9 staff: quoting pages and the "Acting as" picker.
 * `admin` — additionally the Admin panel.
 */
export type SessionRole = 'admin' | 'user'

export interface AdminStatus {
  unlocked: boolean
  /** null when signed out. */
  role: SessionRole | null
  /** False when neither key is configured on the deploy — don't offer the form. */
  available: boolean
  adminAvailable: boolean
  userAvailable: boolean
  /** Decision 57: Entra SSO configured on this deploy — offer "Sign in with Microsoft". */
  ssoAvailable: boolean
  /** The human behind an SSO session; null for key sessions. */
  who?: { name: string; email: string } | null
  /** Which partner this session is currently working as (decision 34). */
  actingAs?: { id: string; name: string; mode: string } | null
  /** Decision 136: a signed-in PARTNER (PIN) session — role-less, one partner. */
  partnerSession?: { person: string; email: string; partner: { id: string; name: string; mode: PartnerMode } } | null
}

// ---------------------------------------------------------------- partner sign-in (decision 136)

export const requestPin = (email: string) =>
  send<{ message: string }>('/api/auth-request-pin', 'POST', { email })
export const verifyPin = (email: string, pin: string) =>
  send<{ partner: { id: string; name: string; mode: PartnerMode } }>('/api/auth-verify-pin', 'POST', { email, pin })

export async function adminStatus(): Promise<AdminStatus> {
  try {
    return await get<AdminStatus>('/api/admin-unlock')
  } catch {
    return { unlocked: false, role: null, available: false, adminAvailable: false, userAvailable: false, ssoAvailable: false, who: null, actingAs: null }
  }
}

export const adminSignIn = (key: string, as: SessionRole) =>
  send<void>('/api/admin-unlock', 'POST', { key, as })
export const adminSignOut = () => send<void>('/api/admin-unlock', 'DELETE')

// ---------------------------------------------------------------- product lines

export interface ProductLinesResponse {
  editable: boolean
  lines: LineSummary[]
  /** Decision 178: per-line provisioning routing (asset Service Type + pipeline). */
  routing: Record<string, { serviceType: string | null; assetPipelineId: string | null }>
  serviceTypeOptions: string[]
  assetPipelines: Array<{ id: string; label: string }>
}

export const fetchProductLines = () => get<ProductLinesResponse>('/api/product-lines')

export const saveProductLine = (update: {
  name: string; discountPct?: number; channelVisible?: boolean
  /** Decision 178: '' clears the routing; undefined leaves it untouched. */
  serviceType?: string; assetPipelineId?: string
}) =>
  send<void>('/api/product-lines', 'PATCH', update)

// ---------------------------------------------------------------- partners

export interface PartnerRow {
  id: string
  name: string
  mode: PartnerMode
  tierId: string | null
  tierName: string | null
  quotePrefix: string
  referralFee: number | null
  isTest: boolean
  active: boolean
  quoteCount: number
  /** Decision 133: issuer/branding fields (reply_to, logo_url, addresses…). */
  branding: Record<string, string>
}

export interface PartnersResponse {
  editable: boolean
  partners: PartnerRow[]
  tiers: Tier[]
}

export const fetchPartners = () => get<PartnersResponse>('/api/partners')
export const createPartner = (p: unknown) => send<void>('/api/partners', 'POST', p)
export const updatePartner = (p: unknown) => send<void>('/api/partners', 'PATCH', p)

/** Decision 133: merge branding keys (empty string removes a key). */
export const updatePartnerBranding = (id: string, branding: Record<string, string>) =>
  send<void>('/api/partners', 'PATCH', { id, branding })

/** Decision 133: raw-body PNG/JPEG upload — the server magic-byte checks it. */
export async function uploadPartnerLogo(partnerId: string, file: File): Promise<{ logoUrl: string }> {
  const res = await fetch(`/api/partners/logo?id=${encodeURIComponent(partnerId)}`, { method: 'POST', body: file })
  const body = (await res.json().catch(() => ({}))) as { logoUrl?: string; error?: string }
  if (!res.ok) throw new Error(body.error ?? `Upload failed (${res.status})`)
  return { logoUrl: String(body.logoUrl) }
}

/** Decision 139: the reseller's quote-pack cover sheet — raw-body PDF upload. */
export async function uploadPartnerCover(partnerId: string, file: File): Promise<{ coverUrl: string }> {
  const res = await fetch(`/api/partners/cover?id=${encodeURIComponent(partnerId)}`, { method: 'POST', body: file })
  const body = (await res.json().catch(() => ({}))) as { coverUrl?: string; error?: string }
  if (!res.ok) throw new Error(body.error ?? `Upload failed (${res.status})`)
  return { coverUrl: String(body.coverUrl) }
}

/** Decision 34: a Wave9 session works as a partner. null steps back out. */
export const actAs = (partnerId: string | null) => send<void>('/api/act-as', 'POST', { partnerId })

// ---------------------------------------------------------------- partner people

/** Recorded now, ENFORCED when RBAC lands — see migration 007. */
export type PersonRole = 'owner' | 'quoter' | 'viewer'

export interface PersonRow {
  id: string
  partnerId: string
  salutation: string | null
  firstName: string
  lastName: string
  name: string
  email: string
  role: string
  phone: string | null
  /** Decision 88: HubSpot owner id override for the deal sync (internal people). */
  hsOwnerId: string | null
  /** Decision 89: what an SSO sign-in gets — admin | staff | none. */
  portalRole: string
  isStaff: boolean
  active: boolean
  lastLogin: string | null
  quoteCount: number
}

export interface PeopleResponse {
  editable: boolean
  roles: PersonRole[]
  people: PersonRow[]
}

export const fetchPeople = () => get<PeopleResponse>('/api/partner-users')
export const createPerson = (p: unknown) => send<void>('/api/partner-users', 'POST', p)
export const updatePerson = (p: unknown) => send<void>('/api/partner-users', 'PATCH', p)
/** Deactivates. There is no hard delete — quotes and audit rows point here. */
export const deactivatePerson = (id: string) =>
  send<void>(`/api/partner-users?id=${encodeURIComponent(id)}`, 'DELETE')

// ---------------------------------------------------------------- scheduled jobs

export interface JobRunResult {
  ok: boolean
  dryRun: boolean
  counts: Record<string, number>
  warnings: string[]
  error?: string
  message?: string
  /** Decision 194: the run was handed to the background function — the history row is the result. */
  started?: boolean
  background?: boolean
}

/** Decision 194: is the deal-sync SCHEDULER alive — judged on scheduled runs only. */
export interface ScheduleHealth {
  lastScheduledAt: string | null
  ageMinutes: number | null
  stale: boolean
  lastAnyAt: string | null
  lastAnyTrigger: string | null
  lastKickAt: string | null
  lastNotifiedAt: string | null
}

export interface JobDescriptor {
  key: string
  label: string
  description: string
  /** null for manual-only jobs. */
  schedule: string | null
  supportsDryRun: boolean
  dryRunMeans?: string
  /** Set when the job consumes an uploaded file — the page renders a picker. */
  upload?: { accept: string; label: string }
  /** Decision 194: real runs go to the background function; the button says "started". */
  background?: boolean
  /** Decision 199: the section the job sits in on the Scheduled jobs page. */
  group?: 'scheduled' | 'tools' | 'oneoff'
  lastRun: Record<string, unknown> | null
}

/** Decision 184: one queued deal sync, with the error that has been failing it. */
export interface DealSyncQueueRow {
  quoteId: string
  quoteNumber: string
  title: string | null
  status: string
  partnerName: string
  dealId: string | null
  reason: string
  attempts: number
  lastError: string | null
  nextAttemptAt: string
  updatedAt: string
}

export interface JobsStatus {
  canRun: boolean
  jobs: JobDescriptor[]
  runs: Array<Record<string, unknown>>
  catalogue: { total: number; active: number; lastSyncedAt: string | null }
  queue?: { rows: DealSyncQueueRow[]; total: number; truncated: boolean }
  /** Decision 194: scheduler verdict (null pre-migration / on error). */
  schedule?: ScheduleHealth | null
}

/**
 * Decision 187: which partner owns this quote? Staff-only, identity only —
 * a deep link needs it to switch acting-as before opening the builder.
 */
export const locateQuote = (quoteId: string) =>
  get<{ quoteId: string; partnerId: string; partnerName: string; quoteNumber: string }>(
    `/api/quotes/${encodeURIComponent(quoteId)}/locate`,
  )

export const fetchJobs = () => get<JobsStatus>('/api/admin-jobs')
export const runJob = (job: string, dryRun: boolean, payload?: string) =>
  send<JobRunResult>('/api/admin-jobs', 'POST', payload === undefined ? { job, dryRun } : { job, dryRun, payload })

/** Decision 184: make one queued row due now — the next sweep takes it. */
export const retryDealSync = (quoteId: string) =>
  send<{ ok: true; quoteId: string }>('/api/admin-jobs', 'POST', { action: 'retry-sync', quoteId })

/** Decision 185: stop retrying one row — logged on the quote's activity trail. */
export const ignoreDealSync = (quoteId: string, reason: string) =>
  send<{ ok: true; quoteId: string }>('/api/admin-jobs', 'POST', { action: 'ignore-sync', quoteId, reason })

// ---------------------------------------------------------------- portal context

/**
 * Who the portal is working as, and what it can switch to.
 *
 * ⚠️ The ACTIVE partner is server-side state (the session's actingAsPartnerId),
 * not a local dropdown value. Changing it must go through /api/act-as or the
 * server keeps refusing partner-scoped requests while the UI happily shows a
 * partner selected — exactly how the catalogue ended up returning 409.
 */
export interface PortalContext {
  isStaff: boolean
  role: SessionRole | null
  partners: Partner[]
  activePartnerId: string | null
}

export async function fetchPortalContext(): Promise<PortalContext> {
  const status = await adminStatus()
  if (!status.unlocked) {
    // Decision 136: a partner (PIN) session is SIGNED IN, with exactly one
    // partner — their own. isStaff here has always really meant "signed in";
    // the pages branch on role (null = partner) as before.
    if (status.partnerSession) {
      const p = status.partnerSession.partner
      return {
        isStaff: true,
        role: null,
        partners: [{
          id: p.id, name: p.name, mode: p.mode, tierId: null,
          quotePrefix: '', referralFee: null, branding: {}, isTest: false, active: true,
        }],
        activePartnerId: p.id,
      }
    }
    return { isStaff: false, role: null, partners: [], activePartnerId: null }
  }

  const list = await fetchPartners()
  return {
    isStaff: true,
    role: status.role,
    partners: list.partners.filter((p) => p.active).map((r) => ({
      id: r.id, name: r.name, mode: r.mode, tierId: r.tierId,
      quotePrefix: r.quotePrefix, referralFee: r.referralFee,
      branding: {}, isTest: r.isTest, active: r.active,
    })),
    activePartnerId: status.actingAs?.id ?? null,
  }
}

export const selectPartner = (partnerId: string | null) => actAs(partnerId)

// ---------------------------------------------------------------- health

/** Decision 173: one feature-gating settings group on the status page. */
export interface FeatureCheck { feature: string; label: string; configured: boolean; missing: string[] }

/** Decision 173: the last run of one scheduled-job family, with its age. */
export interface JobRecencyRow {
  family: string
  lastJob: string
  ok: boolean | null
  dryRun: boolean
  at: string | null
  ageMinutes: number | null
  stale: boolean
  error: string | null
}

export interface HealthReport {
  ok: boolean
  /** Decision 173: when the report was computed — the status strip shows it. */
  checkedAt?: string
  blockers: string[]
  hints?: string[]
  checks: Record<string, unknown> & {
    features?: { checked: boolean; features: FeatureCheck[] }
    stageMap?: { checked: boolean; rows: number; missing: string[] }
    jobs?: { checked: boolean; jobs: JobRecencyRow[] }
    queues?: {
      checked: boolean
      dealSync?: { total: number; due: number; stuck: number }
      acceptancePdf?: { total: number }
      emailStitch?: { total: number }
    }
    machineBridge?: { portalMachineSecret: boolean }
  }
}

/** Decision 173: always cache-busted — /api/health taught us stale bodies lie (2026-08-16). */
export const fetchHealth = () => get<HealthReport>(`/api/health?t=${Date.now()}`)

// ------------------------------------------- integration checks (admin only)
// Both endpoints also accept ?key=DEBUG_KEY for curl during setup; from the
// portal the admin session cookie is the credential.

export interface Probe {
  ok: boolean
  status?: number
  detail: string
  hint?: string
}

export interface HubspotCheckReport {
  ok: boolean
  writesEnabled: boolean
  failed: string[]
  checks: Record<string, Probe>
  pipelines: Array<{ pipelineId: string; label: string; stages: Array<{ stageId: string; label: string }> }> | null
  catalogue: {
    total: number; active: number; inactive: number
    recurring: number; oneOff: number; recurringWithoutTerm: number
    monthlyBilled: Array<{ sku: string | null; name: string; frequency: BillingFrequency }>
    zeroPriced: number; multiLineProducts: number
    productLines: Array<{ name: string; count: number }>
    withAnyOverride: number
  } | null
  note?: string
}

export const runHubspotCheck = () => get<HubspotCheckReport>('/api/hubspot-check')

export interface MailCheckReport {
  ok: boolean
  checks: {
    token: Probe
    identities: { partnerQuotes: string; internalQuotes: string }
    mailboxes?: Record<string, Probe>
    sendTest?: Record<string, Probe> | string
    portalEmailEvents: {
      last7Days?: Record<string, number>
      lastFailure?: { at: string; error: unknown; to: unknown; hint?: string } | null
      note?: string
    }
  }
  notes?: string[]
}

export const runMailCheck = (opts: { send?: string; from?: string } = {}) => {
  const params = new URLSearchParams()
  if (opts.send) params.set('send', opts.send)
  if (opts.from) params.set('from', opts.from)
  const qs = params.toString()
  return get<MailCheckReport>(`/api/mail-check${qs ? `?${qs}` : ''}`)
}

// ---------------------------------------------------------------- board pack (decision 205)

export interface BoardPackListItem {
  id: string
  month: string
  monthLabel: string
  rows: number
  updatedAt: string | null
  updatedBy: string | null
}

export interface BoardFamily {
  name: string
  boardFamily: string | null
}

export interface BoardCandidate {
  kind: 'quote' | 'booking'
  id: string
  quoteNumber: string | null
  name: string
  title: string | null
  tcv: number
  status: string | null
  stageLabel: string | null
  ownerName: string | null
  partnerName: string | null
  acceptedAt: string | null
  sentAt: string | null
  sites: number | null
}

export interface BoardRowInput {
  section?: BoardSection
  quoteId?: string | null
  historicId?: string | null
  name?: string | null
  family?: string | null
  tcv?: number | null
  termMonths?: number | null
  liveBy?: string | null
  notes?: string | null
  sites?: number | null
  sort?: number | null
}

export const listBoardPacks = () =>
  get<{ packs: BoardPackListItem[]; admin: boolean; currentMonth: string }>('/api/board-pack')
export const createBoardPack = (month: string) =>
  send<{ ok: true; id: string; created: boolean; carried: number }>('/api/board-pack', 'POST', { month })
export const fetchBoardPack = (id: string) => get<{ pack: BoardPackView; admin: boolean }>(`/api/board-pack/${id}`)
export const deleteBoardPack = (id: string) => send<{ ok: true }>(`/api/board-pack/${id}`, 'DELETE')
export const fetchBoardSettings = () =>
  get<{ families: BoardFamily[]; editable: boolean }>('/api/board-pack/settings')
export const saveBoardSettings = (payload: { families?: BoardFamily[] }) =>
  send<{ ok: true; familiesUpdated: number }>('/api/board-pack/settings', 'PATCH', payload)
export const fetchBoardCandidates = (id: string, section: BoardSection, q: string) =>
  get<{ candidates: BoardCandidate[] }>(`/api/board-pack/${id}/candidates?section=${section}&q=${encodeURIComponent(q)}`)
export const addBoardRow = (id: string, row: BoardRowInput) => send<{ ok: true; id: string }>(`/api/board-pack/${id}/rows`, 'POST', row)
export const updateBoardRow = (id: string, rowId: string, row: BoardRowInput) =>
  send<{ ok: true }>(`/api/board-pack/${id}/rows/${rowId}`, 'PATCH', row)
export const deleteBoardRow = (id: string, rowId: string) => send<{ ok: true }>(`/api/board-pack/${id}/rows/${rowId}`, 'DELETE')
/** The slide as a file — a plain navigation, the browser saves it. */
export const boardPackPptxUrl = (id: string) => `/api/board-pack/${id}/pptx`
/** Decision 260: the pack as the group board pack's capture workbook (.xlsx). */
export const boardPackExportUrl = (id: string) => `/api/board-pack/${id}/export`

// ---------------------------------------------------------------- Billing (decision 223, Round A)

export type { BillingLine, ScheduleInvoice, ScheduleLine, Frequency, Destination } from '@shared/billing'
import type { BillingLine, ScheduleInvoice, ScheduleLine, Frequency, Destination } from '@shared/billing'

export interface BillingQueueRow {
  dealId: string
  dealName: string
  amount: number | null
  stageId: string
  stageLabel: string
  interim: boolean
  /** Decision 225: a Process Rollover deal — a finished contract to extend. */
  rollover: boolean
  enteredAt: string | null
  invCompany: string | null
  invoiceStackCount: number
  arr: number | null
  setup: number | null
  quoteNumber: string | null
  profile: null | { id: string; status: string; linesTotal: number; linesBilled: number; updatedAt: string; billToName: string | null; invoicesTotal: number; invoicesSent: number; nextDraft: string | null }
  /** Decision 228: a created, unfinished schedule whose deal has left the queue stages — shown under "Schedules started" only. */
  offQueue: boolean
  url: string
}
export interface BillingQueue {
  rows: BillingQueueRow[]
  saturated: boolean
  tiles: { count: number; value: number; arr: number; setup: number; ready: number; interim: number; notStarted: number; withProfile: number; rollovers: number; adopted: number; completed: number; unmatched: number }
  adoption: AdoptProgress | null
}
export interface AdoptProgress { armed: boolean; mode: 'drafts' | 'history'; phase: 'drafts' | 'sent' | 'deals' | 'adopt' | 'done'; page: number; pending: number; done: number; adopted: number; failed: number; lastWarning: string | null; rateLimit: { problem: string; until: string } | null; drafts: number; startedAt: string; finishedAt: string | null }
export interface AdoptedRow { dealId: string; dealName: string; billToName: string | null; invoices: number; drafts: number; nextDraftDate: string | null; adoptedAt: string | null; url: string }
export interface UnmatchedDraft { xeroInvoiceId: string; number: string | null; contactName: string | null; invoiceDate: string | null; reference: string | null; total: number | null; reason: string; dealId: string | null; url: string }
export interface BillingProfile {
  id: string
  dealId: string
  dealName: string
  dealUrl: string
  quoteId: string | null
  quoteNumber: string | null
  companyId: string | null
  companyName: string | null
  stageId: string | null
  stageLabel: string | null
  amount: number | null
  status: 'draft' | 'created' | 'adopted'
  invCompany: string | null
  invEmail: string | null
  poRef: string | null
  invoiceStackCount: number
  trustCompanyId: string | null
  trustName: string | null
  billTo: { contactId: string | null; name: string | null; source: 'inv_company' | 'school' | 'trust' | 'manual'; termsDays: number; stored: boolean; candidates: XeroContact[] }
  createDate: string
  destination: Destination
  proposedDestination: Destination
  contractStart: string | null
  contractEnd: string | null
  termMonths: number | null
  datesOverridden: boolean
  /** Decision 243: the grouping window for later-year invoices (months) — the profile's own or the portal default. */
  groupMonths: number
  groupMonthsDefault: number
  groupMonthsOverridden: boolean
  proposedWindow: { start: string; end: string; termMonths: number } | null
  reference: string
  vatPct: number
  lines: Array<BillingLine & { hsLineItemId: string | null; hsProductId: string | null; pushedAt: string | null; alignMode: 'own' | 'align' | 'align_prorata'; rolloverOf: string | null }>
  /** Decision 225: the rollover context and history. */
  rollover: { inStage: boolean; complete: boolean; adoptedFrom: 'xero' | 'hubspot_lines' | null; history: BillingRollover[] }
  /** Decision 224: value-bearing line changes, newest first. */
  variations: BillingVariation[]
  generated: ScheduleInvoice[]
  working: WorkingInvoice[]
  warnings: string[]
  xeroConnected: boolean
  updatedAt: string
  /** Round B */
  lastPushAt: string | null
  hubspotWarning: string | null
  statusCheckedAt: string | null
  pushed: { invoices: number; total: number; linesUnbilled: number }
}
/** Round B: a working invoice that is (or failed to be) in Xero carries its Xero side. */
export interface WorkingInvoice extends ScheduleInvoice {
  xero?: {
    invoiceId: string; number: string | null; status: string; url: string; pushedAt: string | null; editedSincePush: boolean
    amountPaid: number | null; paidAt: string | null; lastReadAt: string | null; historyNoted: boolean
  }
  pushError?: string
}
export interface CreatePreview {
  ok: boolean; blockers: string[]; warnings: string[]; contact: { id: string | null; name: string | null }
  toCreate: number; toUpdate: number; alreadyPushed: number; unchanged: number; gross: number; linesUnbilled: number
  destination: Destination; willMoveDeal: boolean; unknownItems: string[]
}
export interface CreateResult {
  ok: boolean; created: number; updated: number; failed: Array<{ seq: number; error: string }>
  hubspot: { taskCompleted: number; stageMoved: boolean; propertiesWritten: number; warning: string | null }
  warnings: string[]; profile: BillingProfile | null
}
export const fetchCreatePreview = (dealId: string) => get<{ preview: CreatePreview }>(`/api/billing/deals/${encodeURIComponent(dealId)}/create`)
export const createBillingInvoices = (dealId: string) => send<CreateResult>(`/api/billing/deals/${encodeURIComponent(dealId)}/create`, 'POST', { confirm: true })
export const checkBillingStatus = (dealId: string) => send<{ run: { ok: boolean; message?: string; error?: string; warnings: string[] }; profile: BillingProfile }>(`/api/billing/deals/${encodeURIComponent(dealId)}/status`, 'POST')
export interface BillingLinePatch {
  id?: string; name?: string; sku?: string | null; nominalCode?: string | null; qty?: number; unitPrice?: number
  frequency?: Frequency; payments?: number; serviceStart?: string | null; billed?: boolean; remove?: boolean; hsProductId?: string | null
}
export interface BillingVariation {
  id: string
  kind: 'remove' | 'add' | 'requantity'
  lineId: string | null
  hsLineItemId: string | null
  sku: string | null
  name: string
  frequency: string
  before: { qty: number | null; unitPrice: number | null; payments: number | null }
  after: { qty: number | null; unitPrice: number | null; payments: number | null }
  valueDelta: number
  reason: string
  hubspotOutcome: string | null
  invoicesRebuilt: number
  invoicesRepushed: number
  periodsKept: number
  createdBy: string
  createdAt: string
}
export interface BillingVariationsMonth { month: string; rows: Array<BillingVariation & { dealId: string; dealName: string; dealUrl: string }>; net: number; count: number }
export interface BillingPatch {
  createDate?: string
  poRef?: string | null
  billTo?: { contactId: string; name: string; source: 'inv_company' | 'school' | 'trust' | 'manual'; termsDays?: number; remember?: boolean }
  destination?: Destination
  contract?: { start: string | null; end: string | null; termMonths: number | null } | 'proposed'
  lines?: BillingLinePatch[]
  /** Decision 224: required when `lines` removes, adds or re-prices a line. */
  reason?: string
  /** 223g: lines not yet in Xero — their own invoices, or aligned onto the drafts already in Xero. */
  newLinesMode?: 'own' | 'align' | 'align_prorata'
  /** Decision 243: months within which later-year invoices share one invoice; 0 = none; null = the portal default. Regenerates. */
  groupMonths?: number | null
  working?: Array<{ seq: number; invoiceDate: string; lines: Array<Omit<ScheduleLine, 'net' | 'prorataDays' | 'fullPeriodAmount' | 'edited'>> }>
  regenerate?: boolean
  resetWorking?: boolean
}
export const fetchBillingQueue = () => get<BillingQueue>('/api/billing/queue')
export const fetchBillingProfile = (dealId: string) => get<{ profile: BillingProfile }>(`/api/billing/deals/${encodeURIComponent(dealId)}`)
export const saveBillingProfile = (dealId: string, patch: BillingPatch) =>
  send<{ profile: BillingProfile }>(`/api/billing/deals/${encodeURIComponent(dealId)}`, 'PATCH', patch)
export const refreshBillingProfile = (dealId: string) => send<{ profile: BillingProfile }>(`/api/billing/deals/${encodeURIComponent(dealId)}/refresh`, 'POST')
export const searchBillingContacts = (q: string) => get<{ contacts: XeroContact[] }>(`/api/billing/contacts?q=${encodeURIComponent(q)}`)
export interface CatalogueHit { hsProductId: string; sku: string | null; name: string; nominalCode: string | null; unitPrice: number; frequency: Frequency; payments: number; termMonths: number | null }
export const searchBillingProducts = (q: string) => get<{ products: CatalogueHit[] }>(`/api/billing/products?q=${encodeURIComponent(q)}`)
export const fetchAdoptedSchedules = (q: string) => get<{ rows: AdoptedRow[]; total: number }>(`/api/billing/adopted?q=${encodeURIComponent(q)}`)
export interface CompletedRow { dealId: string; dealName: string; billToName: string | null; invoices: number; paid: number; lastInvoiceDate: string | null; contractEnd: string | null; status: string; adoptedFrom: string | null; url: string }
export type CompletedSort = 'contract_end' | 'last_invoice'
export const fetchCompletedSchedules = (q: string, sort: CompletedSort = 'contract_end', dir: 'asc' | 'desc' = 'desc') =>
  get<{ rows: CompletedRow[]; total: number }>(`/api/billing/completed?q=${encodeURIComponent(q)}&sort=${sort}&dir=${dir}`)
export const fetchUnmatchedDrafts = () => get<{ drafts: UnmatchedDraft[] }>('/api/billing/unmatched')
export interface BillingRollover {
  id: string; months: number; invoices: number; value: number
  lines: Array<{ lineId: string; newLineId: string; name: string; sku: string | null; qty: number; unitPrice: number; oldUnitPrice: number; payments: number; from: string; to: string }>
  contractEndBefore: string | null; contractEndAfter: string | null; hubspotOutcome: string | null; createdBy: string; createdAt: string
}
export interface BillingRolloversMonth { month: string; rows: Array<BillingRollover & { dealId: string; dealName: string; dealUrl: string }>; count: number; value: number }
export interface RolloverInput { months: number; lines: Array<{ lineId: string; unitPrice?: number }>; reason?: string }
export interface RolloverPreview {
  ok: boolean; months: number
  lines: Array<{ lineId: string; name: string; sku: string | null; frequency: Frequency; frequencyLabel: string; qty: number; unitPrice: number; oldUnitPrice: number; payments: number; from: string; to: string; net: number; hsLineItemId: string | null; currentEnd: string }>
  invoices: ScheduleInvoice[]; net: number
  contractEndBefore: string | null; contractEndAfter: string | null; termBefore: number | null; termAfter: number | null
  priceChanges: number; destinationLabel: string; blockers: string[]; warnings: string[]
}
export interface RolloverResult { ok: boolean; rolloverId: string | null; create: CreateResult | null; warnings: string[]; profile: BillingProfile | null }
export const previewBillingRollover = (dealId: string, input: RolloverInput) => send<{ preview: RolloverPreview }>(`/api/billing/deals/${encodeURIComponent(dealId)}/rollover`, 'POST', input)
export const applyBillingRollover = (dealId: string, input: RolloverInput) => send<RolloverResult>(`/api/billing/deals/${encodeURIComponent(dealId)}/rollover`, 'POST', { ...input, confirm: true })
export const fetchBillingRollovers = (month: string) => get<BillingRolloversMonth>(`/api/billing/rollovers?month=${encodeURIComponent(month)}`)
export const fetchBillingVariations = (month: string) => get<BillingVariationsMonth>(`/api/billing/variations?month=${encodeURIComponent(month)}`)
/** Decision 234: the address (from GIAS or typed) and phone ride with the new contact. */
export interface NewContactAddress { line1: string; line2: string; town: string; county: string; postcode: string }
export const createBillingContact = (name: string, email: string | null, address?: NewContactAddress | null, phone?: string | null) =>
  send<{ contact: XeroContact }>('/api/billing/contacts', 'POST', { name, email, address: address ?? null, phone: phone ?? null })
